import { z } from 'zod';
import { PRODUCTION_TZ_OFFSET_MINUTES, addDays, shiftEndUtc, shiftStartUtc, weekStart, } from './time.js';
/**
 * График смен.
 *
 * Система помогает, но не решает за администратора: автоматического составления
 * графика нет. Есть проверки, которые видно прямо в сетке, — назначить вопреки
 * жёлтой подсветке можно, вопреки красной лучше не стоит.
 */
/** Минимум часов отдыха между сменами. Меньше — подсвечиваем. */
export const MIN_REST_HOURS = 12;
function windowFor(context, siteId, shiftNumber) {
    return context.windows.get(siteId)?.find((window) => window.shiftNumber === shiftNumber);
}
function intervalOf(context, assignment) {
    const window = windowFor(context, assignment.siteId, assignment.shiftNumber);
    if (!window)
        return null;
    const offset = context.offsetMinutes ?? PRODUCTION_TZ_OFFSET_MINUTES;
    return {
        start: shiftStartUtc(assignment.date, window, offset).getTime(),
        end: shiftEndUtc(assignment.date, window, offset).getTime(),
    };
}
export function weekDays(start) {
    return Array.from({ length: 7 }, (_, index) => addDays(start, index));
}
/**
 * Все проверки сетки разом.
 *
 * Порядок в списке не важен: интерфейс раскладывает их по ячейкам и сотрудникам.
 */
export function checkSchedule(context) {
    const issues = [];
    const days = weekDays(context.weekStart);
    const dayset = new Set(days);
    const inWeek = context.assignments.filter((assignment) => dayset.has(assignment.date));
    // ── Заполненность агрегатов ────────────────────────────────────────────
    for (const machine of context.machines) {
        const windows = context.windows.get(machine.siteId) ?? [];
        for (const day of days) {
            for (const window of windows) {
                const people = inWeek.filter((assignment) => assignment.machineId === machine.id &&
                    assignment.date === day &&
                    assignment.shiftNumber === window.shiftNumber);
                if (people.length < machine.minOperators) {
                    issues.push({
                        kind: 'UNDERSTAFFED',
                        severity: 'error',
                        message: people.length === 0
                            ? `${machine.name}: смена не закрыта`
                            : `${machine.name}: людей меньше нормы (${people.length} из ${machine.minOperators})`,
                        siteId: machine.siteId,
                        machineId: machine.id,
                        date: day,
                        shiftNumber: window.shiftNumber,
                    });
                }
                if (people.length > machine.maxOperators) {
                    issues.push({
                        kind: 'OVERSTAFFED',
                        severity: 'error',
                        message: `${machine.name}: людей больше нормы (${people.length} из ${machine.maxOperators})`,
                        siteId: machine.siteId,
                        machineId: machine.id,
                        date: day,
                        shiftNumber: window.shiftNumber,
                    });
                }
            }
        }
    }
    // ── Допуски ────────────────────────────────────────────────────────────
    const accessSet = new Set(context.access.map((item) => `${item.userId}:${item.siteId}`));
    for (const assignment of inWeek) {
        if (!accessSet.has(`${assignment.userId}:${assignment.siteId}`)) {
            issues.push({
                kind: 'NO_ACCESS',
                severity: 'error',
                message: 'Нет допуска к этому участку',
                userId: assignment.userId,
                siteId: assignment.siteId,
                machineId: assignment.machineId,
                date: assignment.date,
                shiftNumber: assignment.shiftNumber,
            });
        }
    }
    // ── Подтверждённые отсутствия ──────────────────────────────────────────
    for (const assignment of inWeek) {
        const absent = context.absences.some((absence) => absence.userId === assignment.userId &&
            absence.date === assignment.date &&
            (absence.shiftNumber === null || absence.shiftNumber === assignment.shiftNumber));
        if (absent) {
            issues.push({
                kind: 'ABSENT',
                severity: 'error',
                message: 'Сотрудник отсутствует — нужна замена',
                userId: assignment.userId,
                siteId: assignment.siteId,
                machineId: assignment.machineId,
                date: assignment.date,
                shiftNumber: assignment.shiftNumber,
            });
        }
    }
    // ── Пожелания по выходным ──────────────────────────────────────────────
    const wishSet = new Set(context.wishes.map((wish) => `${wish.userId}:${wish.date}`));
    for (const assignment of inWeek) {
        if (wishSet.has(`${assignment.userId}:${assignment.date}`)) {
            issues.push({
                kind: 'WISH_BROKEN',
                severity: 'warning',
                message: 'Сотрудник просил этот день выходным',
                userId: assignment.userId,
                siteId: assignment.siteId,
                machineId: assignment.machineId,
                date: assignment.date,
                shiftNumber: assignment.shiftNumber,
            });
        }
    }
    // ── Пересечения и отдых ────────────────────────────────────────────────
    const byUser = new Map();
    for (const assignment of inWeek) {
        const list = byUser.get(assignment.userId) ?? [];
        list.push(assignment);
        byUser.set(assignment.userId, list);
    }
    for (const [userId, list] of byUser) {
        const intervals = list
            .map((assignment) => ({ assignment, interval: intervalOf(context, assignment) }))
            .filter((item) => item.interval !== null)
            .sort((a, b) => a.interval.start - b.interval.start);
        for (let index = 1; index < intervals.length; index += 1) {
            const previous = intervals[index - 1];
            const current = intervals[index];
            if (current.interval.start < previous.interval.end) {
                issues.push({
                    kind: 'CONFLICT',
                    severity: 'error',
                    message: 'Сотрудник уже назначен на смену в это же время',
                    userId,
                    siteId: current.assignment.siteId,
                    machineId: current.assignment.machineId,
                    date: current.assignment.date,
                    shiftNumber: current.assignment.shiftNumber,
                });
                continue;
            }
            const restHours = (current.interval.start - previous.interval.end) / (60 * 60 * 1000);
            if (restHours < MIN_REST_HOURS) {
                issues.push({
                    kind: 'SHORT_REST',
                    severity: 'warning',
                    message: `Между сменами ${Math.round(restHours)} ч отдыха вместо ${MIN_REST_HOURS}`,
                    userId,
                    siteId: current.assignment.siteId,
                    machineId: current.assignment.machineId,
                    date: current.assignment.date,
                    shiftNumber: current.assignment.shiftNumber,
                });
            }
        }
        // ── Выходные ─────────────────────────────────────────────────────────
        const workedDays = new Set(list.map((assignment) => assignment.date));
        if (workedDays.size >= days.length) {
            issues.push({
                kind: 'NO_DAYOFF',
                severity: 'warning',
                message: 'За неделю нет ни одного выходного',
                userId,
            });
        }
    }
    return issues;
}
/** Сколько смен у каждого сотрудника — чтобы видеть перекос нагрузки. */
export function countShiftsPerUser(assignments) {
    const counts = new Map();
    for (const assignment of assignments) {
        counts.set(assignment.userId, (counts.get(assignment.userId) ?? 0) + 1);
    }
    return counts;
}
export const DEFAULT_WISH_WINDOW = {
    openDayOfWeek: 4,
    closeDayOfWeek: 0,
    closeTime: '23:59',
};
/**
 * Неделя, на которую сейчас собирают пожелания, — всегда следующая.
 * Сбор идёт с четверга по воскресенье текущей недели.
 */
export function targetWeekFor(today) {
    return addDays(weekStart(today), 7);
}
/** Сколько дней выходных максимум можно попросить. */
export const MAX_WISHES_PER_WEEK = 2;
// ───────────────────────────── события ─────────────────────────────
export const wishCollectionStartedSchema = z.object({
    weekStart: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    closesAt: z.string().datetime({ offset: true }),
});
export const dayOffWishSchema = z.object({
    weekStart: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    /** Дни, которые сотрудник просит выходными. Пустой список — «мне всё равно». */
    dates: z.array(z.string().regex(/^\d{4}-\d{2}-\d{2}$/)).max(MAX_WISHES_PER_WEEK),
    comment: z.string().trim().max(500).nullish(),
});
export const assignmentSchema = z.object({
    siteId: z.string().uuid(),
    machineId: z.string().uuid(),
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    shiftNumber: z.number().int().min(1).max(3),
    userId: z.string().uuid(),
});
export const scheduleDraftUpdatedSchema = z.object({
    weekStart: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    /** Полный состав назначений на неделю: черновик заменяется целиком. */
    assignments: z.array(assignmentSchema).max(2000),
});
export const schedulePublishedSchema = z.object({
    weekStart: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
});
export const scheduleChangedSchema = z.object({
    weekStart: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    assignments: z.array(assignmentSchema).max(2000),
    /** Каждое изменение опубликованного графика объясняется. */
    reason: z.string().trim().min(3).max(500),
});
export const absenceRequestSchema = z.object({
    /**
     * За кого записан невыход. Пусто — за себя.
     *
     * Сам работник кнопку невыхода больше не видит: на видном месте она делает
     * невыход слишком лёгким. Не вышедшего записывает мастер своего участка,
     * поэтому невыход и стал возможен не только за себя.
     */
    userId: z.string().uuid().optional(),
    /** Смена, на которую сотрудник не выйдет. */
    siteId: z.string().uuid(),
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    shiftNumber: z.number().int().min(1).max(3),
    reason: z.string().trim().min(3).max(500),
    /**
     * true — экстренный невыход: доступен всегда, без ограничения по времени.
     * Смысл ограничения в 48 часов — дисциплинировать плановые отгулы,
     * а не заставить заболевшего звонить мимо системы.
     */
    unplanned: z.boolean(),
});
export const absenceDecisionSchema = z.object({
    absenceId: z.string().uuid(),
    approved: z.boolean(),
    comment: z.string().trim().max(500).nullish(),
});
//# sourceMappingURL=schedule.js.map